import { Link } from "react-router-dom";
import { LogoMark } from "@mineoptics/brand/logo";
import { PillLink } from "./PillLink";
import { CONTACT_EMAIL, DOCS_URL, LOGIN_URL, SIGNUP_URL, isExternal } from "@/lib/site";
import { useReveals } from "@/lib/motion";

const COLUMNS = [
  {
    heading: "Platform",
    links: [
      { label: "Sites & drillholes", to: "/features/sites-drillholes" },
      { label: "Block models & pit shell", to: "/features/block-models" },
      { label: "Drill & blast", to: "/features/drill-blast" },
      { label: "Cost model", to: "/features/cost-model" },
      { label: "Reports & history", to: "/features/reports" },
      { label: "Operations", to: "/features/operations" },
      { label: "Vendors & quotes", to: "/features/vendors-quotes" },
      { label: "Pricing", to: "/pricing" },
    ],
  },
  {
    heading: "Resources",
    links: [
      { label: "Documentation", to: DOCS_URL },
      { label: "API reference", to: `${DOCS_URL}/api-reference/overview` },
      { label: "Formulas", to: `${DOCS_URL}/reference/formulas` },
      { label: "Sign in", to: LOGIN_URL },
    ],
  },
  {
    heading: "Company",
    links: [
      { label: "About", to: "/about" },
      { label: "Careers", to: "/careers" },
      { label: "Contact", to: "/contact" },
      { label: "Security", to: "/security" },
    ],
  },
  {
    heading: "Legal",
    links: [
      { label: "Privacy", to: "/privacy" },
      { label: "Terms", to: "/terms" },
    ],
  },
];

const linkClass = "text-[0.9375rem] text-fg/70 transition-colors duration-300 hover:text-fg";

export function Footer() {
  const scope = useReveals<HTMLElement>();

  return (
    <footer ref={scope} data-theme="dark" className="relative overflow-hidden bg-bg">
      <div className="shell pb-8 pt-20 md:pt-28">
        <div className="grid gap-12 lg:grid-cols-[1.1fr_1fr]">
          <div>
            <h2 data-split className="title max-w-[12ch]">
              Know what every tonne costs.
            </h2>
            <div data-fade className="mt-10 flex flex-wrap items-center gap-3">
              <PillLink to={SIGNUP_URL} label="Start free" variant="accent" arrow />
              <PillLink to={`mailto:${CONTACT_EMAIL}`} label={CONTACT_EMAIL} variant="outline" />
            </div>
          </div>

          <div data-fade className="grid grid-cols-2 gap-x-6 gap-y-12 sm:grid-cols-4">
            {COLUMNS.map((column) => (
              <div key={column.heading}>
                <h3 className="eyebrow text-mute">{column.heading}</h3>
                <ul className="mt-5 space-y-3">
                  {column.links.map((link) => (
                    <li key={link.label}>
                      {isExternal(link.to) ? (
                        <a href={link.to} className={linkClass}>
                          {link.label}
                        </a>
                      ) : (
                        <Link to={link.to} className={linkClass}>
                          {link.label}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <span data-rule className="rule mt-16" />

        <div className="mt-6 flex flex-col gap-3 text-mute sm:flex-row sm:items-center sm:justify-between">
          <span className="eyebrow">© {new Date().getFullYear()} MineOptics</span>
          <span className="eyebrow">Cost modelling for open-pit studies</span>
        </div>
      </div>

      {/* Oversized wordmark, cropped by the bottom edge of the page. */}
      <div aria-hidden="true" className="pointer-events-none flex select-none items-end justify-center gap-[2vw] px-5">
        <LogoMark className="mb-[1.6vw] h-[11vw] w-[11vw] shrink-0 text-fg/90" />
        <span className="-mb-[3.2vw] whitespace-nowrap text-[15.5vw] font-medium leading-none tracking-[-0.06em] text-fg/90">
          MineOptics
        </span>
      </div>
    </footer>
  );
}
